import { Head } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface Stats {
  users_count: number;
}

interface RegistrationData {
  date: string;
  count: number;
}

interface Abilities {
  view_user_stats: boolean;
  view_registrations_chart: boolean;
}

interface Props {
  stats: Stats;
  registrations_chart: RegistrationData[];
  abilities: Abilities;
}

export default function DashboardIndex({ stats, registrations_chart, abilities }: Props) {
  return (
    <>
      <Head title="لوحة التحكم" />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">لوحة التحكم</h1>
          <p className="text-muted-foreground mt-1">مرحبا بك في لوحة التحكم</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Users Stats Card */}
          {abilities.view_user_stats && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span>المستخدمين</span>
                  <Users className="h-5 w-5 text-muted-foreground" />
                </CardTitle>
                <CardDescription>إجمالي المستخدمين المسجلين</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.users_count}</div>
                <p className="text-xs text-muted-foreground mt-2">مستخدم نشط</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Registrations Chart */}
        {abilities.view_registrations_chart && registrations_chart.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>تسجيلات المستخدمين</CardTitle>
              <CardDescription>عدد المستخدمين المسجلين يومياً في آخر 30 يوماً</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={registrations_chart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    interval={Math.floor(registrations_chart.length / 7) || 0}
                  />
                  <YAxis />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(value) => [`${value} مستخدم`, "التسجيلات"]}
                    labelFormatter={(label) => `التاريخ: ${label}`}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(var(--primary))"
                    name="التسجيلات"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
